"use client"

import React from 'react'
import { userGuideData } from './Data/UserGuideData'
import { useMobileContext } from './MobileContext'
import { Card, Typography, Divider, Steps, Space, Alert } from 'antd'
import { useTranslation } from 'react-i18next'

const { Title, Text, Paragraph } = Typography

/** `List` component của antd sẽ bị gỡ bỏ hẳn (không chỉ đổi tên prop) — thay bằng render thường, giữ
 * đường kẻ ngăn cách giữa các dòng giống mặc định của `List`. */
const renderPlainList = (items: string[], render: (item: string, idx: number) => React.ReactNode) => (
  <div>
    {items.map((item, idx) => (
      <div
        key={idx}
        style={{ padding: '8px 0', borderBottom: idx < items.length - 1 ? '1px solid #f0f0f0' : 'none' }}
      >
        {render(item, idx)}
      </div>
    ))}
  </div>
)

const UserGuide: React.FC = () => {
  const isMobile = useMobileContext()
  const { t } = useTranslation()

  return (
    <Card className={!isMobile ? 'p-6' : 'p-2'}>
      <Title level={2}>{t(userGuideData.title)}</Title>
      
      <div className="mt-6">
        {/* Introduction */}
        <div>
          {userGuideData.introduction.content.map((paragraph, idx) => (
            <Paragraph key={idx} className="text-base">{t(paragraph)}</Paragraph>
          ))}
        </div>
        
        {/* Requirements */}
        <div className="mt-6">
          <Title level={3}>{t("common.requirement")}</Title>
          <div>
            {userGuideData.introduction.requirements.map((req, idx) => (
              <div
                key={idx}
                style={{
                  padding: '8px 0',
                  borderBottom:
                    idx < userGuideData.introduction.requirements.length - 1
                      ? '1px solid #f0f0f0'
                      : 'none',
                }}
              >
                <Space orientation="vertical" size={0}>
                  <Text strong>{t(req.title)}</Text>
                  <Text>{t(req.description)}</Text>
                </Space>
              </div>
            ))}
          </div>
        </div>
        
        {/* Tips */}
        <div className="mt-6">
          <Alert
            title={t("esim.setting_guide_tips")}
            description={renderPlainList(userGuideData.introduction.tips, (tip) => (
              <Text>{t(tip)}</Text>
            ))}
            type="info"
            showIcon
          />
        </div>
        
        {/* Installation for each platform */}
        <div className="mt-6">
          {userGuideData.steps.map((platform, platformIdx) => (
            <div key={platformIdx} className="mb-6">
              <Title level={3}>{t(platform.title)}</Title>
              
              {platform.methods.map((method, methodIdx) => (
                <div key={methodIdx} className="mb-4">
                  <Title level={4} className="mt-4">{t(method.title)}</Title>
                  <Steps
                    orientation={isMobile ? "vertical" : "horizontal"}
                    current={-1}
                    items={method.steps.map((step, stepIdx) => ({
                      title: `${t("common.step")} ${stepIdx + 1}`,
                      content: t(step),
                    }))}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        
        {/* Activation */}
        <div className="mt-6">
          <Title level={3}>{t("esim.setting_guide_active_esim")}</Title>
          
          <Title level={4} className="mt-4">iOS</Title>
          {renderPlainList(userGuideData.activation.ios, (step, idx) => (
            <Text>{`${idx + 1}. ${t(step)}`}</Text>
          ))}

          <Title level={4} className="mt-4">Android</Title>
          {renderPlainList(userGuideData.activation.android, (step, idx) => (
            <Text>{`${idx + 1}. ${t(step)}`}</Text>
          ))}
        </div>

        {/* Deletion */}
        <div className="mt-6">
          <Title level={3}>{t("esim.setting_guide_delete_esim")}</Title>

          <Title level={4} className="mt-4">iOS</Title>
          {renderPlainList(userGuideData.deletion.ios, (step, idx) => (
            <Text>{`${idx + 1}. ${t(step)}`}</Text>
          ))}

          <Title level={4} className="mt-4">Android</Title>
          {renderPlainList(userGuideData.deletion.android, (step, idx) => (
            <Text>{`${idx + 1}. ${t(step)}`}</Text>
          ))}
        </div>
      </div>
    </Card>
  )
}

export default UserGuide